import { EventEmitter, Inject, Injectable } from "@angular/core";

import { Calculation, Client } from "@salaxy/core";

import { ClientNg } from "../api/ClientNg";
import { SessionService } from "../account/SessionService";
import { IRepository } from "./IRepository";

/**
 * Implements the salaxy repository pattern for Calculations stored for the current account.
 */
@Injectable()
export class CalculationRepository implements IRepository<Calculation> {

    /** List of items in the repository */
    public list: Calculation[] = [];

    /** Currently selected item in the repository */
    public current: Calculation = null;

    private changeEvent: EventEmitter<Calculation> = new EventEmitter<Calculation>();

    /** Creates a new instance of CalculationRepository with dependency injection. */
    constructor(
        @Inject(ClientNg) private client: Client,
    ) { }

    /**
     * Assures the repository initialization:
     * Initialization is done after successful authentication.
     * Typically the call to fetch the repository list, but may fetch other stuff from the server.
     *
     * @param session - The session service that contains information about the authenticated user.
     */
    public init(session: SessionService) {
        this.reloadList();
    }

    /**
     * Reloads the list from the server - called e.g. after delete and add new
     * @param callback - Optional callback that is called after save and susequent reloadList completes.
     */
    public reloadList(callback?: () => any): void {
        this.client.calculationsApi.getAll().then( (calcs) => {
            this.list = calcs;
            if (callback) {
                callback();
            }
        } );
    }

    /**
     * Gets a new blank object with default values, suitable for UI binding.
     * This is a synchronous method - does not go to the server and it is not 100% reliable in that way.
     * However, it shoud provide a basic object, good enough for most views.
     */
    public getBlank(): Calculation {
        // TODO: Go through and make really compatible (with enums etc.) -
        // consider basing on "new Calculation()" constructor
        // TODO: Move to js namespace
        const blank = {
            employer: {
                accountId: null,
                avatar: {
                    entityType: "undefined" as any,
                    id: null,
                    initials: "?",
                },
                isSelf: true,
            },
            framework: {
                type: "notDefined" as any,
            },
            id: null,
            info: {
                workEndDate: null,
                workStartDate: null,
            },
            owner: null,
            result: null,
            rows: [],
            salary: {
                amount: 1.0,
                kind: null,
                price: null,
                unit: null,
            },
            worker: {
                accountId: null,
                avatar: {
                    entityType: "person" as any,
                    id: null,
                    initials: "?",
                },
                isReadyForPayment: false,
                paymentData: {},
            },
            workflow: {
                status: "draft" as any,
            },
        };
        return blank;
    }

    /**
     * Sets the current repository item.
     * If not loaded, starts loading from the server to the current.
     * @param id - Identifier of the calculation
     */
    public setCurrentId(id: string) {
        this.client.calculationsApi.get(id).then( (calc) => this.setCurrent(calc));
    }

    /**
     * Sets the current calculation
     * @param item - Item to set as current
     */
    public setCurrent(item: Calculation) {
        this.current = item;
        this.changeEvent.emit(this.current);
    }

    /**
     * Creates a new item and sets it as current for editing and saving.
     * The item is not yet sent to server and thus it is not in the list, nor does it have an identifier.
     */
    public newCurrent() {
        this.setCurrent(this.getBlank());
    }

    /**
     * Saves changes to the current item.
     * @param callback - Optional callback that is called after save and susequent reloadList completes.
     */
    public saveCurrent(callback?: () => any): void {
        if (this.current) {
            this.save(this.current, callback);
        } else {
            // console.error("SaveCurrent called when current is not set.");
        }
    }

    /**
     * Adds or updates a given item.
     * Operation is determined based on id: null/''/'new' adds, other string values update.
     * @param item - Item that is updated.
     * @param callback - Optional callback that is called after save and susequent reloadList completes.
     */
    public save(item: Calculation, callback?: () => any): void {
        this.client.calculationsApi.createOrUpdate(item)
        .then((savedObject) => {
            this.setCurrent(savedObject);
            this.reloadList(callback);
        });
    }

    /**
     * Deletes the given item from repository if possible
     * @param id - Identifier of the item to delete
     * @param callback - Optional callback that is called after save and susequent reloadList completes.
     */
    public delete(id: string, callback?: () => any) {
        this.client.calculationsApi.deleteCalc(id)
            .then(() => {
                this.reloadList(callback);
            });
    }

    /**
     * Recalculates the current item
     * @param callback - Optional callback that is called with the recalculated
     * calculation when the recalculation is done.
     */
    public recalculateCurrent(callback?: (calc?: Calculation) => any) {
        this.client.calculatorApi.recalculate(this.current).then((calc) => {
            this.setCurrent(calc);
            if (callback) {
                callback(calc);
            }
        });
    }

    /**
     * Sets the union paymen
     * @param paymentType Type of the payment (which union and subtype)
     * @param unionPaymentPercent Percent of the union
     */
    public setUnionPayment(paymentType: any, unionPaymentPercent: number) {
        //  HACK: There should be enumeration for paymentType.
        this.client.calculatorApi.setUnionPayment(this.current, paymentType, unionPaymentPercent).then((calc) => {
            this.setCurrent(calc);
        });
    }

    /**
     * Returns change emitter.
     */
    public onChange() {
        return this.changeEvent;
    }
}
